import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import type { RootState } from "../redux/store";
import { toggle } from "../redux/navReducer";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "../contexts/ThemeContext";
import { adminRoutes } from "../routers/AdminRouters";
import Assets from "../core/assets";
import { setAdminLogout } from "../services/admin/adminReducer";
import { useGetProfileQuery, useLogoutMutation } from "../services";

const Navbar = () => {
  const dispatch = useAppDispatch();
  const [logoutApi] = useLogoutMutation();
  type NavbarSliceState = { isOpen: boolean };
  const isOpen = useAppSelector(
    (state: RootState) => (state.navbar as NavbarSliceState).isOpen,
  );
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  // NEW: selected organisation id
  const [selectedOrgId, setSelectedOrgId] = useState<string>("");

  // NEW: treat "closed" as collapsed icon-rail
  const isCollapsed = !isOpen;

  // NEW: make filled icons green in both themes
  const fillColor = "#B1C223";
  // NEW: light theme hover fill is white
  const lightHoverFillColor = "#FFFFFF";

  // Pick logo based on theme
  const displayLogo =
    theme === "light" ? Assets.authLightLogo : Assets.authDarkLogo;
  // type admin selector with RootState
  const { admin, isLoggedIn } = useAppSelector(
    (state: RootState) => state.admin,
  );

  const { data: profileResp, refetch } = useGetProfileQuery(undefined, {
    skip: !isLoggedIn,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  useEffect(() => {
    if (isLoggedIn) refetch();
  }, [isLoggedIn, refetch]);

  const profile = admin ?? profileResp?.result ?? profileResp;

  const rawRole =
    (profile as any)?.role ??
    (profile as any)?.data?.role ??
    (profile as any)?.user?.role;
  const isSuperAdmin =
    typeof rawRole === "string" &&
    rawRole.toLowerCase().replace(/[\s_-]+/g, "") === "superadmin";

  const MaskedIcon: React.FC<{
    src: string;
    filled: boolean;
    filledSrc?: string;
    className?: string;
    alt?: string;
  }> = ({ src, filled, filledSrc, className }) => {
    const maskUrl = filled && filledSrc ? filledSrc : src;

    // Exact color logic (no filters)
    // - Light: default green, hover/active white
    // - Dark: default gray, hover/active green
    const defaultDark = "#9CA3AF";
    const currentFillColor =
      theme === "light"
        ? filled
          ? lightHoverFillColor
          : fillColor
        : filled
          ? fillColor
          : defaultDark;

    return (
      <div className={`relative w-5 h-5 ${className || ""}`}>
        <span
          className="absolute inset-0 block transition-colors duration-200"
          style={{
            backgroundColor: currentFillColor,
            // Standard mask
            maskImage: `url(${maskUrl})`,
            maskRepeat: "no-repeat",
            maskPosition: "center",
            maskSize: "contain",
            // WebKit mask
            WebkitMaskImage: `url(${maskUrl})`,
            WebkitMaskRepeat: "no-repeat",
            WebkitMaskPosition: "center",
            WebkitMaskSize: "contain",
          }}
          aria-hidden="true"
        />
      </div>
    );
  };

  const handleToggle = () => {
    dispatch(toggle());
  };

  const handleCloseSidebar = () => {
    if (isOpen) dispatch(toggle());
  };

  const goTo = (path: string) => {
    navigate(path);
    handleCloseSidebar();
  };

  const handleLogout = async () => {
    try {
      await logoutApi().unwrap();
    } catch (error) {
      console.log("logout error", error);
    }
    dispatch(setAdminLogout());
    navigate(adminRoutes.login);
  };

  const isActiveRoute = (path: string) => {
    return location.pathname === path;
  };
  const isAdminActive = location.pathname.startsWith(
    `${adminRoutes.adminList}`,
  );
  const showAdminSub = isOpen && (adminMenuOpen || hoveredItem === "admin");
  const isOrganisationActive = location.pathname.startsWith(
    `${adminRoutes.organisationList}`,
  );
  const showOrganisationSub =
    isOpen && (isOrganisationActive || hoveredItem === "organisation");
  const isGymActive = location.pathname.startsWith(`${adminRoutes.gymList}`);
  const showGymSub = isOpen && (isGymActive || hoveredItem === "gym");
  const isStaffActive = location.pathname.startsWith(
    `${adminRoutes.staffList}`,
  );
  const showStaffSub = isOpen && (isStaffActive || hoveredItem === "staff");
  const isSubscriberActive =
    location.pathname.startsWith(`${adminRoutes.subscriberList}`) ||
    location.pathname.startsWith(`${adminRoutes.segmentsList}`) ||
    location.pathname.startsWith(`${adminRoutes.tiersList}`);
  const showSubscriberSub =
    isOpen && (isSubscriberActive || hoveredItem === "subscribers");
  const isSubscriptionsActive = location.pathname.startsWith(
    `${adminRoutes.subscriberList}`,
  );
  const showSubscriptionsSub =
    isOpen && (isSubscriptionsActive || hoveredItem === "subscriptions");
  const isPlansActive = location.pathname.startsWith(`${adminRoutes.planList}`);
  const showPlansSub = isOpen && (isPlansActive || hoveredItem === "plans");
  const isTaxActive = location.pathname.startsWith(`${adminRoutes.tax}`);
  const showTaxSub = isOpen && (isTaxActive || hoveredItem === "tax");
  const isWorkoutActive = location.pathname.startsWith(
    `${adminRoutes.exerciseList}`,
  );
  const showWorkoutSub =
    isOpen && (isWorkoutActive || hoveredItem === "workout");
  const isWorkoutLibraryActive =
    location.pathname.startsWith(`${adminRoutes.categoryList}`) ||
    location.pathname.startsWith(`${adminRoutes.equipmentList}`) ||
    location.pathname.startsWith(`${adminRoutes.formatList}`) ||
    location.pathname.startsWith(`${adminRoutes.groupList}`) ||
    location.pathname.startsWith(`${adminRoutes.metricsList}`) ||
    location.pathname.startsWith(`${adminRoutes.musclesList}`);
  const showWorkoutLibrarySub =
    isOpen && (isWorkoutLibraryActive || hoveredItem === "workoutLibrary");

  const isThemeSettingsActive = location.pathname.startsWith(
    `${adminRoutes.themeSettings}`,
  );

  // Label helper: inline when expanded, tooltip when collapsed
  const SideLabel: React.FC<{ text: string; className?: string }> = ({
    text,
    className,
  }) =>
    isOpen ? (
      <span className={`flex-1 whitespace-nowrap ${className || ""}`}>
        {text}
      </span>
    ) : (
      <span
        className={`absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 rounded-md text-xs shadow whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none ${
          theme === "dark"
            ? "bg-gray-800 text-white"
            : "bg-white text-gray-900 border border-gray-200"
        } ${className || ""}`}
      >
        {text}
      </span>
    );

  // NEW: theme-aware divider for section separation (used when collapsed)
  const Divider: React.FC = () => (
    <div
      className={`${isOpen ? "mx-3" : "mx-1"} my-2 border-b ${
        theme === "dark" ? "border-gray-800" : "border-gray-200"
      }`}
    />
  );

  return (
    <>
      {/* Main Navbar */}
      <nav
        className={`w-full h-16 flex items-center justify-between px-6 fixed top-0 z-50 border-b transition-colors duration-200 ${
          theme === "dark"
            ? "bg-[#1a1a1a] border-gray-700 text-white"
            : "bg-white border-gray-200 text-gray-900"
        }`}
      >
        {/* Left Section */}
        <div className="flex items-center space-x-4">
          {/* <Link to={adminRoutes.dashboard}>
            <button
              type="button"
              onClick={handleToggle}
              title="Toggle sidebar"
              className="flex items-center space-x-2 cursor-pointer focus:outline-none"
              aria-expanded={isOpen}
              aria-controls="app-sidebar"
            >
              {isOpen ? (
                <img src={displayLogo} alt="Gym Stream" className="h-8 w-25" />
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="40"
                  height="28"
                  viewBox="0 0 40 28"
                  fill="none"
                  aria-label="Gym Stream"
                >
                  <path
                    d="M13.1671 0.089942C12.4542 0.089942 11.7791 0.194084 11.1465 0.407102C10.5138 0.62012 9.9237 0.923078 9.38078 1.32071C8.83785 1.71834 8.37519 2.19645 7.99278 2.7503C7.61037 3.30888 7.31294 3.929 7.1005 4.62012L2.88458 18.4473L2.25667 20.497H27.7033L27.5427 21.0935C27.4106 21.4627 27.1839 21.7704 26.8676 22.0071C26.5513 22.2438 26.2067 22.3669 25.8384 22.3669H4.31978C3.55497 22.3669 2.85153 22.5988 2.2189 23.0627C1.58628 23.5266 1.14722 24.142 0.911167 24.9089L0 27.929H26.32C27.7222 27.929 28.978 27.5172 30.0874 26.6982C31.1969 25.8793 31.9617 24.7905 32.3866 23.4367L34.9643 14.9302H34.9596L36.6733 9.30651L37.2115 7.5361L37.2304 7.47929H18.601C18.3886 7.47929 18.1809 7.50296 17.9826 7.5361C17.4538 7.63077 16.9628 7.83906 16.5002 8.17515C15.8675 8.63906 15.4285 9.25444 15.1924 10.0213L14.2813 13.0414H29.9788L29.9599 13.0982L29.4217 14.9065H9.47992L11.7508 7.53136L11.9396 6.91598C12.0718 6.54675 12.289 6.23905 12.5958 6.00237C12.898 5.76568 13.2521 5.6426 13.6486 5.6426H35.1342C35.9274 5.6426 36.6403 5.42012 37.2729 4.96568C37.9055 4.51598 38.3304 3.90533 38.5429 3.13846L39.454 0.0757408H13.1671V0.089942Z"
                    fill="#C6D92C"
                  />
                </svg>
              )}
            </button>
          </Link> */}
        </div>

        {/* Center Section - Search */}
        {/* <div className="flex-1 max-w-md mx-8">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M10.5 16.5C11.2879 16.5 12.0682 16.3448 12.7961 16.0433C13.5241 15.7417 14.1855 15.2998 14.7427 14.7426C15.2998 14.1855 15.7418 13.5241 16.0433 12.7961C16.3448 12.0681 16.5 11.2879 16.5 10.5C16.5 9.71207 16.3448 8.93185 16.0433 8.2039C15.7418 7.47595 15.2998 6.81451 14.7427 6.25736C14.1855 5.70021 13.5241 5.25825 12.7961 4.95672C12.0682 4.65519 11.2879 4.5 10.5 4.5C8.90872 4.5 7.38259 5.13214 6.25737 6.25736C5.13216 7.38258 4.50002 8.9087 4.50002 10.5C4.50002 12.0913 5.13216 13.6174 6.25737 14.7426C7.38259 15.8679 8.90872 16.5 10.5 16.5ZM16.82 15.406L20.4 18.986C20.4955 19.0783 20.5716 19.1887 20.6239 19.3108C20.6762 19.4328 20.7037 19.564 20.7048 19.6968C20.7058 19.8296 20.6804 19.9613 20.63 20.0841C20.5797 20.207 20.5053 20.3186 20.4114 20.4124C20.3174 20.5062 20.2057 20.5804 20.0828 20.6306C19.9599 20.6808 19.8282 20.706 19.6954 20.7047C19.5626 20.7035 19.4314 20.6758 19.3095 20.6233C19.1875 20.5708 19.0772 20.4946 18.985 20.399L15.405 16.819C13.7975 18.0668 11.7748 18.6552 9.74877 18.4642C7.72273 18.2732 5.84562 17.3173 4.49957 15.7911C3.15351 14.2648 2.4397 12.2829 2.50344 10.2489C2.56718 8.2149 3.40368 6.28162 4.84266 4.84265C6.28164 3.40367 8.21492 2.56717 10.2489 2.50343C12.283 2.43968 14.2648 3.1535 15.7911 4.49955C17.3173 5.8456 18.2732 7.72271 18.4642 9.74875C18.6552 11.7748 18.0669 13.7975 16.819 15.405L16.82 15.406Z"
                  fill="#EDF5C0"
                />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search for..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 rounded-lg border transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#B6CB24] ${
                theme === "dark"
                  ? "bg-gray-800 border-gray-600 text-white placeholder-gray-400"
                  : "bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500"
              }`}
            />
          </div>
        </div> */}

        {/* Right Section */}
        <div className="flex items-center space-x-4">
          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className={`relative w-20 h-8 rounded-full transition-all duration-300 flex items-center ${
              theme === "dark"
                ? "bg-gray-700 border border-gray-600"
                : "bg-gray-200 border border-gray-300"
            }`}
          >
            <div
              className={`absolute w-6 h-6 rounded-full transition-all duration-300 flex items-center justify-center ${
                theme === "dark"
                  ? "translate-x-12 bg-gray-800"
                  : "translate-x-1 bg-[#B6CB24]"
              }`}
            >
              {theme === "dark" ? (
                <img
                  src={Assets.darkModeIcon}
                  alt="Dark Mode"
                  className="w-4 h-4"
                />
              ) : (
                <svg
                  className="w-3 h-3 text-gray-800"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414-1.414zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </div>
            <span
              className={`absolute left-2 text-xs font-medium transition-opacity duration-300 ${
                theme === "dark" ? "opacity-100 text-gray-300" : "opacity-0"
              }`}
            >
              DARK
            </span>
            <span
              className={`absolute right-2 text-xs font-medium transition-opacity duration-300 ${
                theme === "light" ? "opacity-100 text-gray-600" : "opacity-0"
              }`}
            >
              LIGHT
            </span>
          </button>

          {/* Notifications */}
          {/* <button
            className={`relative p-2 rounded-lg transition-colors duration-200 ${
              theme === "dark"
                ? "hover:bg-gray-700 text-gray-300"
                : "hover:bg-gray-100 text-gray-600"
            }`}
          >
            <img
              src={
                theme === "light"
                  ? Assets.notificationLightIcon
                  : Assets.notificationIcon
              }
              alt="Notifications"
              className="w-8 h-10"
            />
          </button> */}

          {/* Profile */}
          <div className="flex items-center ">
            {/* <button
              type="button"
              aria-label="Profile"
              className={`rounded-full p-0.5 ${
                theme === "dark" ? "hover:bg-gray-800" : "hover:bg-gray-100"
              }`}
            >
              <div className="w-8 h-8 bg-[#B6CB24] rounded-full flex items-center justify-center">
                <img
                  src={Assets.userIcon}
                  alt="User"
                  className="w-4 h-4"
                  style={{ filter: "invert(100%)" }}
                />
              </div>
            </button> */}
          </div>
        </div>
      </nav>

      {/* Sidebar */}
      <div
        id="app-sidebar"
        className={`fixed left-0 top-16 h-[calc(100vh-4rem)] ${
          isOpen ? "w-64" : "w-16"
        } shadow-lg transition-[width] duration-300 ease-in-out z-40 ${
          theme === "dark"
            ? "bg-[#1a1a1a] border-gray-700 text-white"
            : "bg-white border-gray-200 text-gray-900"
        }`}
      >
        <div className="py-4 h-full flex flex-col">
          {/* Navigation Links */}
          <div className={`flex-1 space-y-px ${isOpen ? "px-3" : "px-1"}`}>
            {/* Dashboard */}
            {/* <Link
              to="/"
              className={`group relative flex items-center ${
                isOpen ? "px-3" : "px-2 justify-center"
              } py-2 rounded-lg font-medium transition-all duration-200 ${
                hoveredItem === "dashboard"
                  ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-[#B6CB24]"
                  : theme === "dark"
                  ? "text-gray-300 hover:text-white"
                  : "text-gray-700 hover:text-gray-900"
              }`}
              onMouseEnter={() => setHoveredItem("dashboard")}
              onMouseLeave={() => setHoveredItem(null)}
              onClick={() => {
                handleToggle();
              }}
            >
              <MaskedIcon
                src={Assets.dashboardIcon}
                filled={hoveredItem === "dashboard"}
                filledSrc={Assets.dashboardIconFilled}
                className={`${isOpen ? "mr-3" : ""}`}
                alt="Dashboard"
              />
              <SideLabel text="Dashboard" />
            </Link> */}

            {/* Subscriptions */}
            {/* <div
              className="flex flex-col"
              onMouseEnter={() => setHoveredItem("subscriptions")}
              onMouseLeave={() => setHoveredItem(null)}
            >
              <button
                type="button"
                className={`group  relative flex items-center w-full text-left ${
                  isOpen ? "px-3" : "px-2 justify-center"
                } py-2 rounded-lg font-medium transition-all duration-200 ${
                  isSubscriptionsActive || hoveredItem === "subscriptions"
                    ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-[#B6CB24]"
                    : theme === "dark"
                    ? "text-gray-300 hover:text-white"
                    : "text-gray-700 hover:text-gray-900"
                }`}
                onClick={() => {
                  goTo("/screens");
                  handleToggle();
                }}
                aria-expanded={showSubscriptionsSub}
                aria-controls="subscriptions-submenu"
              >
                <MaskedIcon
                  src={Assets.subscriptionsIcon}
                  filled={
                    hoveredItem === "subscriptions" || isSubscriptionsActive
                  }
                  filledSrc={Assets.subscriptionsIconFilled}
                  className={`${isOpen ? "mr-3 " : ""}`}
                  alt="Subscriptions"
                />
                <SideLabel text="Subscriptions" />
              </button>

              <div
                id="subscriptions-submenu"
                className={`overflow-hidden transition-[max-height,opacity] duration-200 ${
                  showSubscriptionsSub
                    ? "max-h-32 opacity-100"
                    : "max-h-0 opacity-0"
                }`}
              >
                <Link
                  to="/screens"
                  onClick={() => {
                    handleCloseSidebar();
                    handleToggle();
                  }}
                  className={`mt-1 ml-8 flex items-center px-3 py-2 rounded-lg text-sm transition-colors ${
                    isActiveRoute("/screens")
                      ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-none"
                      : theme === "dark"
                      ? "text-gray-300 hover:bg-gray-800 hover:text-white"
                      : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  }`}
                >
                  Screens
                </Link>
                <Link
                  to="billing"
                  onClick={() => {
                    handleCloseSidebar();
                    handleToggle();
                  }}
                  className={`mt-1 ml-8 flex items-center px-3 py-2 rounded-lg text-sm transition-colors ${
                    isActiveRoute("/billing")
                      ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-none"
                      : theme === "dark"
                      ? "text-gray-300 hover:bg-gray-800 hover:text-white"
                      : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  }`}
                >
                  Billing
                </Link>
                <Link
                  to="/payment-details"
                  onClick={() => {
                    handleCloseSidebar();
                    handleToggle();
                  }}
                  className={`mt-1 ml-8 flex items-center px-3 py-2 rounded-lg text-sm transition-colors ${
                    isActiveRoute(adminRoutes.payment)
                      ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-none"
                      : theme === "dark"
                      ? "text-gray-300 hover:bg-gray-800 hover:text-white"
                      : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  }`}
                >
                  Payment Details
                </Link>
              </div>
            </div> */}

            {/* Reports - now using MaskedIcon for exact colors */}
            {/* <Link
              to="/reports"
              className={`group relative flex items-center ${
                isOpen ? "px-3  " : "px-2 justify-center"
              } py-2 rounded-lg font-medium transition-all duration-200 ${
                isActiveRoute("/reports")
                  ? "bg-[#B6CB24] text-black"
                  : hoveredItem === "reports"
                  ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-[#B6CB24]"
                  : theme === "dark"
                  ? "text-gray-300 hover:text-white"
                  : "text-gray-700 hover:text-gray-900"
              }`}
              onMouseEnter={() => setHoveredItem("reports")}
              onMouseLeave={() => setHoveredItem(null)}
            >
              <MaskedIcon
                src={Assets.reportsIcon}
                filled={hoveredItem === "reports" || isActiveRoute("/reports")}
                className={`${isOpen ? "mr-3" : ""}`}
                alt="Reports"
              />
              <SideLabel text="Reports" />
            </Link> */}

            {/* Management Section - only when expanded */}
            {isOpen && (
              <div
                className={`px-3 py-1 text-xs font-semibold uppercase tracking-wider ${
                  theme === "dark" ? "text-gray-500" : "text-gray-400"
                } border-b ${
                  theme === "dark" ? "border-gray-800" : "border-gray-200"
                } mb-2`}
              >
                Management
              </div>
            )}
            {isCollapsed && <Divider />}
            {isSuperAdmin && (
              <div
                className="flex flex-col"
                onMouseEnter={() => setHoveredItem("admin")}
                onMouseLeave={() => setHoveredItem(null)}
              >
                <button
                  type="button"
                  className={`group relative flex items-center w-full text-left ${
                    isOpen ? "px-3" : "px-2 justify-center"
                  } py-2 rounded-lg font-medium transition-all duration-200 ${
                    isAdminActive || hoveredItem === "admin"
                      ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-[#B6CB24]"
                      : theme === "dark"
                        ? "text-gray-300 hover:text-white"
                        : "text-gray-700 hover:text-gray-900"
                  }`}
                  onClick={() => {
                    goTo(adminRoutes.adminList);
                    handleToggle();
                  }}
                  aria-expanded={showAdminSub}
                  aria-controls="admin-submenu"
                >
                  <MaskedIcon
                    src={Assets.adminIcon}
                    filled={isAdminActive || hoveredItem === "admin"}
                    filledSrc={Assets.adminIconFilled}
                    className={`${isOpen ? "mr-3" : ""}`}
                    alt="Admin"
                  />
                  <SideLabel text="Admin" />
                </button>

                {/* Submenu: List (only when expanded) */}
                <div
                  id="admin-submenu"
                  className={`overflow-hidden transition-[max-height,opacity] duration-200 ${
                    showAdminSub ? "max-h-24 opacity-100" : "max-h-0 opacity-0"
                  }`}
                >
                  <Link
                    to={adminRoutes.adminList}
                    onClick={() => {
                      handleCloseSidebar();
                      handleToggle();
                    }}
                    className={`mt-1 ml-8 flex items-center px-3 py-2 rounded-lg text-sm transition-colors ${
                      isActiveRoute(adminRoutes.adminList)
                        ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-none"
                        : theme === "dark"
                          ? "text-gray-300 hover:bg-gray-800 hover:text-white"
                          : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                    }`}
                  >
                    List
                  </Link>
                </div>
              </div>
            )}

            {/* <div
              className="flex flex-col"
              onMouseEnter={() => setHoveredItem("organisation")}
              onMouseLeave={() => setHoveredItem(null)}
            >
              <button
                type="button"
                className={`group relative flex items-center w-full text-left ${
                  isOpen ? "px-3" : "px-2 justify-center"
                } py-2 rounded-lg font-medium transition-all duration-200 ${
                  isOrganisationActive || hoveredItem === "organisation"
                    ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-[#B6CB24]"
                    : theme === "dark"
                    ? "text-gray-300 hover:text-white"
                    : "text-gray-700 hover:text-gray-900"
                }`}
                onClick={() => {
                  goTo(adminRoutes.organisationList);
                  handleToggle();
                }}
                aria-expanded={showOrganisationSub}
                aria-controls="organisation-submenu"
              >
                <MaskedIcon
                  src={Assets.organisationIcon}
                  filled={
                    hoveredItem === "organisation" || isOrganisationActive
                  }
                  filledSrc={Assets.organisationFilled}
                  className={`${isOpen ? "mr-3" : ""}`}
                  alt="Organisation"
                />
                <SideLabel text="Organisation" />
              </button>

              <div
                id="organisation-submenu"
                className={`overflow-hidden transition-[max-height,opacity] duration-200 ${
                  showOrganisationSub
                    ? "max-h-40 opacity-100"
                    : "max-h-0 opacity-0"
                }`}
              >
                <Link
                  to={adminRoutes.organisationList}
                  onClick={() => {
                    handleCloseSidebar();
                    handleToggle();
                  }}
                  className={`mt-1 ml-8 flex items-center px-3 py-2 rounded-lg text-sm transition-colors ${
                    isActiveRoute(adminRoutes.organisationList)
                      ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-none"
                      : theme === "dark"
                      ? "text-gray-300 hover:bg-gray-800 hover:text-white"
                      : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  }`}
                >
                  List
                </Link>
              </div>
            </div> */}

            {/* Gym */}
            {/* <div
              className="flex flex-col"
              onMouseEnter={() => setHoveredItem("gym")}
              onMouseLeave={() => setHoveredItem(null)}
            >
              <button
                type="button"
                className={`group relative flex items-center w-full text-left ${
                  isOpen ? "px-3" : "px-2 justify-center"
                } py-2 rounded-lg font-medium transition-all duration-200 ${
                  isGymActive || hoveredItem === "gym"
                    ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-[#B6CB24]"
                    : theme === "dark"
                    ? "text-gray-300 hover:text-white"
                    : "text-gray-700 hover:text-gray-900"
                }`}
                onClick={() => {
                  goTo(`${adminRoutes.gymList}`);
                  handleToggle();
                }}
                aria-expanded={showGymSub}
                aria-controls="gym-submenu"
              >
                <MaskedIcon
                  src={Assets.gymIcon}
                  filled={hoveredItem === "gym" || isGymActive}
                  filledSrc={Assets.gymIconFilled}
                  className={`${isOpen ? "mr-3" : ""}`}
                  alt="Gym"
                />
                <SideLabel text="Gym" />
              </button>

              <div
                id="gym-submenu"
                className={`overflow-hidden transition-[max-height,opacity] duration-200 ${
                  showGymSub ? "max-h-40 opacity-100" : "max-h-0 opacity-0"
                }`}
              >
                <Link
                  to={adminRoutes.gymList}
                  onClick={() => {
                    handleCloseSidebar();
                    handleToggle();
                  }}
                  className={`mt-1 ml-8 flex items-center px-3 py-2 rounded-lg text-sm transition-colors ${
                    isActiveRoute(`${adminRoutes.gymList}`)
                      ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-none"
                      : theme === "dark"
                      ? "text-gray-300 hover:bg-gray-800 hover:text-white"
                      : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  }`}
                >
                  List
                </Link>
              </div>
            </div> */}

            {/* <div
              className="flex flex-col"
              onMouseEnter={() => setHoveredItem("staff")}
              onMouseLeave={() => setHoveredItem(null)}
            >
              <button
                type="button"
                className={`group relative flex items-center w-full text-left ${
                  isOpen ? "px-3" : "px-2 justify-center"
                } py-2 rounded-lg font-medium transition-all duration-200 ${
                  isStaffActive || hoveredItem === "staff"
                    ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-[#B6CB24]"
                    : theme === "dark"
                    ? "text-gray-300 hover:text-white"
                    : "text-gray-700 hover:text-gray-900"
                }`}
                onClick={() => {
                  goTo(`${adminRoutes.staffList}`);
                  handleToggle();
                }}
                aria-expanded={showStaffSub}
                aria-controls="staff-submenu"
              >
                <MaskedIcon
                  src={Assets.staffIcon}
                  filled={hoveredItem === "staff" || isStaffActive}
                  filledSrc={Assets.staffIconFilled}
                  className={`${isOpen ? "mr-3" : ""}`}
                  alt="Staff"
                />
                <SideLabel text="Staff" />
              </button>

              <div
                id="staff-submenu"
                className={`overflow-hidden transition-[max-height,opacity] duration-200 ${
                  showStaffSub ? "max-h-40 opacity-100" : "max-h-0 opacity-0"
                }`}
              >
                <Link
                  to={adminRoutes.staffList}
                  onClick={() => {
                    handleCloseSidebar();
                    handleToggle();
                  }}
                  className={`mt-1 ml-8 flex items-center px-3 py-2 rounded-lg text-sm transition-colors ${
                    isActiveRoute(adminRoutes.staffList)
                      ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-none"
                      : theme === "dark"
                      ? "text-gray-300 hover:bg-gray-800 hover:text-white"
                      : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  }`}
                >
                  List
                </Link>
              </div>
            </div> */}
            <div
              className="flex flex-col"
              onMouseEnter={() => setHoveredItem("subscribers")}
              onMouseLeave={() => setHoveredItem(null)}
            >
              <button
                type="button"
                className={`group relative flex items-center w-full text-left ${
                  isOpen ? "px-3" : "px-2 justify-center"
                } py-2 rounded-lg font-medium transition-all duration-200 ${
                  isSubscriberActive || hoveredItem === "subscribers"
                    ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-[#B6CB24]"
                    : theme === "dark"
                      ? "text-gray-300 hover:text-white"
                      : "text-gray-700 hover:text-gray-900"
                }`}
                onClick={() => {
                  goTo(`${adminRoutes.subscriberList}`);
                  handleToggle();
                }}
                aria-expanded={showSubscriberSub}
                aria-controls="subscribers-submenu"
              >
                <MaskedIcon
                  src={Assets.staffIcon}
                  filled={hoveredItem === "subscribers" || isSubscriberActive}
                  filledSrc={Assets.staffIconFilled}
                  className={`${isOpen ? "mr-3" : ""}`}
                  alt="Subscribers"
                />
                <SideLabel text="Subscriber Management" />
              </button>

              <div
                id="subscribers-submenu"
                className={`overflow-hidden transition-[max-height,opacity] duration-200 ${
                  showSubscriberSub
                    ? "max-h-64 opacity-100"
                    : "max-h-0 opacity-0"
                }`}
              >
                {[
                  { label: "List", path: adminRoutes.subscriberList },
                  { label: "Segments", path: adminRoutes.segmentsList },
                  { label: "Tiers", path: adminRoutes.tiersList },
                ].map((item) => (
                  <Link
                    key={item.label}
                    to={item.path}
                    onClick={() => {
                      handleCloseSidebar();
                      handleToggle();
                    }}
                    className={`mt-1 ml-8 flex items-center px-3 py-2 rounded-lg text-sm transition-colors ${
                      isActiveRoute(item.path)
                        ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-none"
                        : theme === "dark"
                          ? "text-gray-300 hover:bg-gray-800 hover:text-white"
                          : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Plans */}
            <div
              className="flex flex-col"
              onMouseEnter={() => setHoveredItem("plans")}
              onMouseLeave={() => setHoveredItem(null)}
            >
              <button
                type="button"
                className={`group relative flex items-center w-full text-left ${
                  isOpen ? "px-3" : "px-2 justify-center"
                } py-2 rounded-lg font-medium transition-all duration-200 ${
                  isPlansActive || hoveredItem === "plans"
                    ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-[#B6CB24]"
                    : theme === "dark"
                      ? "text-gray-300 hover:text-white"
                      : "text-gray-700 hover:text-gray-900"
                }`}
                onClick={() => {
                  goTo(adminRoutes.planList);
                  handleToggle();
                }}
                aria-expanded={showPlansSub}
                aria-controls="plans-submenu"
              >
                <MaskedIcon
                  src={Assets.planIcon}
                  filled={isPlansActive || hoveredItem === "plans"}
                  filledSrc={Assets.planIconFilled}
                  className={`${isOpen ? "mr-3" : ""}`}
                  alt="Plans"
                />
                <SideLabel text="Plan" />
              </button>

              <div
                id="plans-submenu"
                className={`overflow-hidden transition-[max-height,opacity] duration-200 ${
                  showPlansSub ? "max-h-24 opacity-100" : "max-h-0 opacity-0"
                }`}
              >
                <Link
                  to={adminRoutes.planList}
                  onClick={() => {
                    handleCloseSidebar();
                    handleToggle();
                  }}
                  className={`mt-1 ml-8 flex items-center px-3 py-2 rounded-lg text-sm transition-colors ${
                    isPlansActive
                      ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-none"
                      : theme === "dark"
                        ? "text-gray-300 hover:bg-gray-800 hover:text-white"
                        : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  }`}
                >
                  List
                </Link>
              </div>
            </div>

            {/* Tax */}
            <div
              className="flex flex-col"
              onMouseEnter={() => setHoveredItem("tax")}
              onMouseLeave={() => setHoveredItem(null)}
            >
              <button
                type="button"
                className={`group relative flex items-center w-full text-left ${
                  isOpen ? "px-3" : "px-2 justify-center"
                } py-2 rounded-lg font-medium transition-all duration-200 ${
                  isTaxActive || hoveredItem === "tax"
                    ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-[#B6CB24]"
                    : theme === "dark"
                      ? "text-gray-300 hover:text-white"
                      : "text-gray-700 hover:text-gray-900"
                }`}
                onClick={() => {
                  goTo(adminRoutes.tax);
                  handleToggle();
                }}
                aria-expanded={showTaxSub}
                aria-controls="tax-submenu"
              >
                <MaskedIcon
                  src={Assets.taxIcon}
                  filled={isTaxActive || hoveredItem === "tax"}
                  filledSrc={Assets.taxIconFilled}
                  className={`${isOpen ? "mr-3" : ""}`}
                  alt="Tax"
                />
                <SideLabel text="Tax" />
              </button>

              <div
                id="tax-submenu"
                className={`overflow-hidden transition-[max-height,opacity] duration-200 ${
                  showTaxSub ? "max-h-24 opacity-100" : "max-h-0 opacity-0"
                }`}
              >
                <Link
                  to={adminRoutes.tax}
                  onClick={() => {
                    handleCloseSidebar();
                    handleToggle();
                  }}
                  className={`mt-1 ml-8 flex items-center px-3 py-2 rounded-lg text-sm transition-colors ${
                    isActiveRoute(adminRoutes.tax)
                      ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-none"
                      : theme === "dark"
                        ? "text-gray-300 hover:bg-gray-800 hover:text-white"
                        : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  }`}
                >
                  List
                </Link>
              </div>
            </div>

            {/* Workout Library */}
            <div
              className="flex flex-col"
              onMouseEnter={() => setHoveredItem("workoutLibrary")}
              onMouseLeave={() => setHoveredItem(null)}
            >
              <button
                type="button"
                className={`group relative flex items-center w-full text-left ${
                  isOpen ? "px-3" : "px-2 justify-center"
                } py-2 rounded-lg font-medium transition-all duration-200 ${
                  isWorkoutLibraryActive || hoveredItem === "workoutLibrary"
                    ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-[#B6CB24]"
                    : theme === "dark"
                      ? "text-gray-300 hover:text-white"
                      : "text-gray-700 hover:text-gray-900"
                }`}
                onClick={() => {
                  goTo(`${adminRoutes.categoryList}`);
                  handleToggle();
                }}
                aria-expanded={showWorkoutLibrarySub}
                aria-controls="workout-library-submenu"
              >
                <MaskedIcon
                  src={Assets.workoutLibraryIcon}
                  filled={
                    hoveredItem === "workoutLibrary" || isWorkoutLibraryActive
                  }
                  filledSrc={Assets.workoutLibraryIconFilled}
                  className={`${isOpen ? "mr-3" : ""}`}
                  alt="Workout Library"
                />
                <SideLabel text="Workout Library" />
              </button>

              <div
                id="workout-library-submenu"
                className={`overflow-hidden transition-[max-height,opacity] duration-200 ${
                  showWorkoutLibrarySub
                    ? "max-h-64 opacity-100"
                    : "max-h-0 opacity-0"
                }`}
              >
                {[
                  { label: "Category", path: adminRoutes.categoryList },
                  { label: "Equipment", path: adminRoutes.equipmentList },
                  { label: "Format", path: adminRoutes.formatList },
                  { label: "Group", path: adminRoutes.groupList },
                  { label: "Metrics", path: adminRoutes.metricsList },
                  { label: "Muscles", path: adminRoutes.musclesList },
                ].map((item) => (
                  <Link
                    key={item.label}
                    to={item.path}
                    onClick={() => {
                      handleCloseSidebar();
                      handleToggle();
                    }}
                    className={`mt-1 ml-8 flex items-center px-3 py-2 rounded-lg text-sm transition-colors ${
                      isActiveRoute(item.path)
                        ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-none"
                        : theme === "dark"
                          ? "text-gray-300 hover:bg-gray-800 hover:text-white"
                          : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Workout */}
            <div
              className="flex flex-col"
              onMouseEnter={() => setHoveredItem("workout")}
              onMouseLeave={() => setHoveredItem(null)}
            >
              <button
                type="button"
                className={`group relative flex items-center w-full text-left ${
                  isOpen ? "px-3" : "px-2 justify-center"
                } py-2 rounded-lg font-medium transition-all duration-200 ${
                  isWorkoutActive || hoveredItem === "workout"
                    ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-[#B6CB24]"
                    : theme === "dark"
                      ? "text-gray-300 hover:text-white"
                      : "text-gray-700 hover:text-gray-900"
                }`}
                onClick={() => {
                  goTo(`${adminRoutes.exerciseList}`);
                  handleToggle();
                }}
                aria-expanded={showWorkoutSub}
                aria-controls="workout-submenu"
              >
                <MaskedIcon
                  src={Assets.workoutIcon}
                  filled={hoveredItem === "workout" || isWorkoutActive}
                  filledSrc={Assets.workoutIconFilled}
                  className={`${isOpen ? "mr-3" : ""}`}
                  alt="Workout"
                />
                <SideLabel text="Workout" />
              </button>

              <div
                id="workout-submenu"
                className={`overflow-hidden transition-[max-height,opacity] duration-200 ${
                  showWorkoutSub ? "max-h-32 opacity-100" : "max-h-0 opacity-0"
                }`}
              >
                <Link
                  to={adminRoutes.exerciseList}
                  onClick={() => {
                    handleCloseSidebar();
                    handleToggle();
                  }}
                  className={`mt-1 ml-8 flex items-center px-3 py-2 rounded-lg text-sm transition-colors ${
                    isActiveRoute(adminRoutes.exerciseList)
                      ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-none"
                      : theme === "dark"
                        ? "text-[#B1C223] hover:bg-gray-800 hover:text-white"
                        : "text-[#B1C223] hover:bg-gray-100 hover:text-gray-900"
                  }`}
                >
                  Exercises
                </Link>
                <Link
                  to={adminRoutes.sessionList}
                  onClick={() => {
                    handleCloseSidebar();
                    handleToggle();
                  }}
                  className={`mt-1 ml-8 flex items-center px-3 py-2 rounded-lg text-sm transition-colors ${
                    isActiveRoute(`${adminRoutes.sessionList}`)
                      ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-none"
                      : theme === "dark"
                        ? "text-gray-300 hover:bg-gray-800 hover:text-white"
                        : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  }`}
                >
                  Sessions
                </Link>
                {/* <Link
                  to={adminRoutes.schedule}
                  onClick={() => {
                    handleCloseSidebar();
                    handleToggle();
                  }}
                  className={`mt-1 ml-8 flex items-center px-3 py-2 rounded-lg text-sm transition-colors ${
                    isActiveRoute(`${adminRoutes.schedule}`)
                      ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-none"
                      : theme === "dark"
                      ? "text-gray-300 hover:bg-gray-800 hover:text-white"
                      : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  }`}
                >
                  Schedule
                </Link> */}
              </div>
            </div>

            {/* Devices */}
            {/* <Link
              to={adminRoutes.deviceList}
              className={`group relative flex items-center ${
                isOpen ? "px-3" : "px-2 justify-center"
              } py-2 rounded-lg font-medium transition-all duration-200 ${
                hoveredItem === "devices"
                  ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-[#B6CB24]"
                  : theme === "dark"
                  ? "text-gray-300 hover:text-white"
                  : "text-gray-700 hover:text-gray-900"
              }`}
              onMouseEnter={() => setHoveredItem("devices")}
              onMouseLeave={() => setHoveredItem(null)}
            >
              <MaskedIcon
                src={Assets.devicesIcon}
                filled={hoveredItem === "devices"}
                filledSrc={Assets.devicesIconFilled}
                className={`${isOpen ? "mr-3" : ""}`}
                alt="Devices"
              />
              <SideLabel text="Devices" />
            </Link> */}

            {/* Theme Settings */}
            <Link
              to={adminRoutes.themeSettings}
              className={`group relative flex items-center ${
                isOpen ? "px-3" : "px-2 justify-center"
              } py-2 rounded-lg font-medium transition-all duration-200 ${
                isThemeSettingsActive || hoveredItem === "themeSettings"
                  ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-[#B6CB24]"
                  : theme === "dark"
                    ? "text-gray-300 hover:text-white"
                    : "text-gray-700 hover:text-gray-900"
              }`}
              onMouseEnter={() => setHoveredItem("themeSettings")}
              onMouseLeave={() => setHoveredItem(null)}
            >
              <MaskedIcon
                src={Assets.settingsIcon}
                filled={
                  hoveredItem === "themeSettings" || isThemeSettingsActive
                }
                filledSrc={Assets.settingsIconFilled}
                className={`${isOpen ? "mr-3" : ""}`}
                alt="Theme Settings"
              />
              <SideLabel text="Theme Settings" />
            </Link>
          </div>

          {/* Logout section pinned to bottom with top border */}
          <div
            className={`mt-auto ${
              isOpen ? "px-3" : "px-1"
            } pt-3 pb-1 border-t ${
              theme === "dark" ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <button
              onClick={handleLogout}
              className={`group relative flex items-center w-full ${
                isOpen ? "px-3" : "px-2 justify-center"
              } py-1 rounded-lg font-medium transition-colors duration-200 ${
                theme === "dark"
                  ? "text-gray-300 hover:text-white"
                  : "text-gray-700 hover:text-gray-900"
              }`}
              onMouseEnter={() => setHoveredItem("logout")}
              onMouseLeave={() => setHoveredItem(null)}
            >
              <MaskedIcon
                src={Assets.logoutIcon}
                filled={hoveredItem === "logout"}
                className={`${isOpen ? "mr-2" : ""}`}
                alt="Logout"
              />
              {isOpen ? (
                <span className="whitespace-nowrap">Logout</span>
              ) : (
                <span
                  className={`absolute left-full top-1/2 -translate-y-1/2 ml-1 px-2 py-1 rounded-md text-xs shadow whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none ${
                    theme === "dark"
                      ? "bg-gray-800 text-white"
                      : "bg-white text-gray-900 border border-gray-200"
                  }`}
                >
                  Logout
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Overlay to close sidebar (keep only when expanded) */}
      {isOpen && (
        <div
          className="fixed top-16 inset-x-0 bottom-0 bg-black/50 z-30"
          onClick={handleToggle}
        />
      )}
    </>
  );
};

export default Navbar;
